import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { storyboardApi } from './storyboardApi';
import StoryboardList from './StoryboardList';
import StoryboardEditor from './StoryboardEditor';

export default function StoryboardModule() {
  const { session } = useAuth();
  const authHeader = useMemo(
    () => (session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    [session?.access_token]
  );
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const loadList = useCallback(async () => {
    if (!session?.access_token) { setLoading(false); return; }
    setLoading(true);
    const { ok, data } = await storyboardApi.list(authHeader);
    setBoards(ok ? (data.storyboards || []) : []);
    setLoading(false);
  }, [authHeader, session?.access_token]);

  useEffect(() => { loadList(); }, [loadList]);

  const handleCreate = async () => {
    setError('');
    setCreating(true);
    const { ok, data } = await storyboardApi.create({ brief: {} }, authHeader);
    setCreating(false);
    if (ok && data.storyboard) {
      setActiveId(data.storyboard.id);
    } else {
      setError(data?.detail || 'Could not create storyboard');
    }
  };

  const handleDelete = async (id) => {
    setBoards(prev => prev.filter(b => b.id !== id));
    await storyboardApi.remove(id, authHeader);
  };

  if (!session?.access_token) {
    return (
      <div className="p-6 text-sm text-gray-400" data-testid="sb-no-session">
        Sign in as a manager to use Storyboard.
      </div>
    );
  }

  if (activeId) {
    return (
      <StoryboardEditor
        storyboardId={activeId}
        authHeader={authHeader}
        onBack={() => { setActiveId(null); loadList(); }}
      />
    );
  }

  return (
    <StoryboardList
      boards={boards}
      loading={loading}
      creating={creating}
      error={error}
      onCreate={handleCreate}
      onOpen={setActiveId}
      onDelete={handleDelete}
    />
  );
}
